import { useCallback, useRef, useState } from 'react';

/**
 * In-memory signed preview URL cache keyed by assetId.
 * URLs are transient UI state only — never persisted in template JSON.
 */
export function createLetterTemplateAssetPreviewCache() {
  /** @type {Map<string, { url: string, expiresAt: number, contentType?: string, width?: number, height?: number }>} */
  const entries = new Map();
  /** @type {Map<string, Promise<unknown>>} */
  const inflight = new Map();

  return {
    get(assetId) {
      const entry = entries.get(assetId);
      if (!entry) return null;
      if (entry.expiresAt <= Date.now()) {
        entries.delete(assetId);
        return null;
      }
      return entry;
    },
    set(assetId, data) {
      entries.set(assetId, data);
    },
    clear(assetId) {
      entries.delete(assetId);
      inflight.delete(assetId);
    },
    getInflight(assetId) {
      return inflight.get(assetId) || null;
    },
    setInflight(assetId, promise) {
      inflight.set(assetId, promise);
    },
    deleteInflight(assetId) {
      inflight.delete(assetId);
    },
  };
}

/**
 * @param {{
 *   fetchPreviewUrl: (assetId: string) => Promise<{ url: string, expiresAt: string | number, contentType?: string, width?: number, height?: number }>,
 *   enabled?: boolean,
 * }} options
 */
export function useLetterTemplateAssetPreviewUrls({ fetchPreviewUrl, enabled = true }) {
  const cacheRef = useRef(createLetterTemplateAssetPreviewCache());
  const [revision, setRevision] = useState(0);

  const bump = useCallback(() => setRevision((v) => v + 1), []);

  const getPreviewState = useCallback(
    (assetId) => {
      if (!assetId) return { status: 'idle', url: null };
      const cached = cacheRef.current.get(assetId);
      if (cached) {
        return { status: 'ready', url: cached.url, expiresAt: cached.expiresAt };
      }
      return { status: 'idle', url: null };
    },
    // revision forces re-read after cache updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revision],
  );

  const requestPreview = useCallback(
    async (assetId, { force = false } = {}) => {
      if (!assetId || !enabled) return null;
      const cache = cacheRef.current;
      if (!force) {
        const existing = cache.get(assetId);
        if (existing) return existing;
        const pending = cache.getInflight(assetId);
        if (pending) return pending;
      } else {
        cache.clear(assetId);
      }

      const promise = fetchPreviewUrl(assetId)
        .then((result) => {
          const expiresAtMs =
            typeof result.expiresAt === 'number'
              ? result.expiresAt
              : Date.parse(String(result.expiresAt || ''));
          const entry = {
            url: result.url,
            expiresAt: Number.isFinite(expiresAtMs) ? expiresAtMs : Date.now() + 15 * 60 * 1000,
            contentType: result.contentType,
            width: result.width,
            height: result.height,
          };
          cache.set(assetId, entry);
          cache.deleteInflight(assetId);
          bump();
          return entry;
        })
        .catch((err) => {
          cache.deleteInflight(assetId);
          bump();
          throw err;
        });

      cache.setInflight(assetId, promise);
      bump();
      return promise;
    },
    [bump, enabled, fetchPreviewUrl],
  );

  const invalidate = useCallback(
    (assetId) => {
      cacheRef.current.clear(assetId);
      bump();
    },
    [bump],
  );

  return { getPreviewState, requestPreview, invalidate, revision };
}

/**
 * Pure cache read for render-time preview URL resolution.
 * Must not trigger network requests or React state updates.
 * @param {(assetId: string) => { status: string, url: string | null }} getPreviewState
 * @param {string | null | undefined} assetId
 * @param {{ impersonating?: boolean }} [options]
 * @returns {string | null}
 */
export function readLetterTemplateAssetPreviewUrl(getPreviewState, assetId, { impersonating = false } = {}) {
  if (!assetId || impersonating) return null;
  const state = getPreviewState(assetId);
  return state.status === 'ready' ? state.url : null;
}
